import type {
  ColumnOrderState,
  GroupingState,
  SortingState,
} from "@tanstack/react-table";
import { selector, selectorFamily, type RecoilValue } from "recoil";

import {
  DECIMAL_RADIX,
  GROUPING_GRID_NO_GROUPS,
  NO_GROUPING_QS_VALUE,
} from "../../constants";
import type {
  IExtendableDto,
  IProduceButtonDto,
  IRideTableDto,
} from "../../dto";
import {
  ProduceButtonsPageViewMode,
  ProduceButtonTodayState,
  SortingEnum,
  StarredDisplayModeEnum,
} from "../../enums";
import {
  filterRidesByUserChanged,
  getOrderedLiveMapDelayStatusGroupValues,
  getSortedGroupValuesFromTable,
  getSpecificSearchPatterMatch,
  getTableFilteredGroupedRowsFromMap,
  guardIsRecoilDefaultValue,
  hasKeys,
  isEntitySelectionKeptOnSearch,
  isEntityServerSideFiltered,
  isEntityServerSidePaginated,
  isRideMapDelayStatusGroupingField,
  parseSpecificSearchStringToArray,
  resolveRideMapDelayStatusGroupingFieldName,
  resolveTableFilteredGroupedRowsMap,
  TableHelpers,
  unique,
  type ITableClientSearch,
  type TTableFilteredGroupedRowsMap,
} from "../../helpers";
import type {
  DtoFieldType,
  IGroupValuePaginationState,
  ITableLayoutProps,
  RydeUiUnit,
} from "../../models";
import {
  gridExpandedAF,
  produceButtonsPageViewModeAtom,
  rideStartEndPositionAtom,
  selectedTableRowsIdsAtomFamily,
  showSelectedOnlyRowsAtomFamily,
  starredDisplayModeAtomFamily,
  starredTableRowsIdsAtomFamily,
  tableColumnsOrderStateAF,
  tableColumnsSizingAF,
  tableColumnsSortingAF,
  tableColumnsVisibilityAF,
  tableGroupingAF,
  tableGroupingDirectionDescAF,
  tableItemPaginationAF,
  tableRowsGroupValuesAtomFamily,
  tableSearchAF,
  tableServerGroupValuesAf,
  userEditOnlyDisplayModeAtomFamily,
} from "../atoms";
import { boundedSelectorFamily } from "../selector-factories";
import {
  filterSearchableControlsSF,
  filterSearchablePhoneFieldNamesSF,
  gridControlsSF,
} from "./dynamic-schema.selectors";
import { currentUserIdSelector } from "./profile.selectors";
import { timezoneSelector } from "./system-settings.selectors";
import { tableRowsComputedSF } from "./table-data.selectors";
import {
  areFiltersPausedByDisplayModeSF,
  areFiltersPausedSF,
  filterCriteriaSF,
} from "./table-filters.selectors";

type TRecoilGet = <T>(recoilValue: RecoilValue<T>) => T;

export const tableLayoutPropsSF = selectorFamily<ITableLayoutProps, RydeUiUnit>(
  {
    key: "tableLayoutPropsSF",
    get:
      entity =>
      ({ get }) => {
        const columnsVisibility = get(tableColumnsVisibilityAF(entity));
        const columnsOrder = get(tableColumnsOrderStateAF(entity));
        const columnsSorting = get(tableColumnsSortingAF(entity));
        const columnsGrouping = get(tableGroupingAF(entity));
        const columnsSizing = get(tableColumnsSizingAF(entity));
        return {
          columnsVisibility,
          columnsOrder,
          columnsSorting,
          columnsGrouping,
          columnsSizing,
        };
      },
  },
);

export const tableColumnWidthSF = selectorFamily<
  number | undefined,
  [entity: RydeUiUnit, columnId: string]
>({
  key: "tableColumnWidthSF",
  get:
    ([entity, columnId]) =>
    ({ get }) =>
      get(tableColumnsSizingAF(entity))[columnId],
});

export const isTableColumnsSizedSF = selectorFamily<boolean, RydeUiUnit>({
  key: "isTableColumnsSizedSF",
  get:
    entity =>
    ({ get }) =>
      hasKeys(get(tableColumnsSizingAF(entity))),
});

export const tableColumnSortingValueSF = selectorFamily<
  SortingEnum,
  [entity: RydeUiUnit, fieldNameResolved: string]
>({
  key: "tableColumnSortingValueSF",
  get:
    ([entity, fieldNameResolved]) =>
    ({ get }) => {
      const columnSorting = get(tableColumnsSortingAF(entity));
      const isSorted =
        columnSorting?.length && columnSorting?.[0].id === fieldNameResolved;

      if (!isSorted) {
        return SortingEnum.NotSorted;
      }

      if (columnSorting[0].desc) {
        return SortingEnum.Desc;
      }

      return SortingEnum.Asc;
    },
});

/**
 * Reach this through `tableRowsFilteredOnceSF` (the grid rows) or `tableRowsFilteredIgnoringSearchOnceSF`
 * (the selection scope), never directly. Its consumers are subscribed at the same time on the rides grid, so
 * a second direct caller runs the whole multi-thousand-row filter twice per invalidation - every filter
 * apply, search batch, starred or selected-only toggle and poll refresh.
 *
 * `isIgnoreSearch` leaves the search box out: the result is the rows a grid would show if nothing were typed,
 * which is what a selection is pruned against on a grid that keeps its checkboxes while searching. See
 * `isEntitySelectionKeptOnSearch`.
 */
const selectTableRowsFilteredOnce = ({
  get,
  entity,
  isIgnoreSearch,
  isIgnoreSelectedOnly,
}: {
  get: TRecoilGet;
  entity: RydeUiUnit;
  isIgnoreSearch?: boolean;
  isIgnoreSelectedOnly?: boolean;
}): IExtendableDto[] => {
  const typeOrmCriteriaObj = get(filterCriteriaSF(entity));
  let rows: IExtendableDto[] = get(tableRowsComputedSF(entity)) ?? [];

  if (!rows.length) {
    return [];
  }

  const searchValue = isIgnoreSearch ? "" : get(tableSearchAF(entity));
  const searchableFilters = get(filterSearchableControlsSF(entity));
  const isServerSideFilteredEntity = isEntityServerSideFiltered(entity);
  const isServerPaginated = isEntityServerSidePaginated(entity);
  const starredDisplayMode = get(starredDisplayModeAtomFamily(entity));
  const skipFilters = isIgnoreSearch
    ? get(areFiltersPausedByDisplayModeSF(entity))
    : get(areFiltersPausedSF(entity));
  const isSearchPatternSpecific = getSpecificSearchPatterMatch(
    searchValue ?? "",
  );

  if (entity === "ride" && !skipFilters) {
    const editedOnlyDisplayMode = get(
      userEditOnlyDisplayModeAtomFamily(entity),
    );
    const currentUserId = get(currentUserIdSelector);
    const timezone = get(timezoneSelector);
    rows = filterRidesByUserChanged({
      resultRows: rows as IRideTableDto[],
      editedOnlyDisplayMode,
      currentUserId,
      timezone,
    });
  } else if (entity === "produceButton") {
    const displayMode = get(produceButtonsPageViewModeAtom);

    if (displayMode === ProduceButtonsPageViewMode.ViewButtonsOfToday) {
      rows = rows.filter(
        row =>
          (row as IProduceButtonDto).todayState ===
          ProduceButtonTodayState.ActiveToday,
      );
    }
  } else if (
    searchValue &&
    isSearchPatternSpecific &&
    !isServerSideFilteredEntity
  ) {
    const searchValueIds = parseSpecificSearchStringToArray(searchValue);

    if (searchValueIds?.length) {
      const searchValueIdsSet = new Set(searchValueIds);
      rows = rows.filter(({ id }) => searchValueIdsSet.has(id));
    }
  }

  const clientFilter = isServerSideFilteredEntity
    ? undefined
    : typeOrmCriteriaObj;

  let limitIds: number[] | undefined;
  const starredOnly =
    starredDisplayMode === StarredDisplayModeEnum.FiltersApplied ||
    starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored;
  const nonStarredOnly =
    starredDisplayMode === StarredDisplayModeEnum.ShowAllWithoutStarred;

  const starredIds = get(starredTableRowsIdsAtomFamily(entity));
  const starredIdsSet = new Set(starredIds);

  if (nonStarredOnly) {
    rows = rows.filter(row => !starredIdsSet.has(row.id));
  }

  if (starredOnly) {
    limitIds = starredIds;
  }

  const shouldShowSelectedOnlyRows =
    !isIgnoreSelectedOnly && get(showSelectedOnlyRowsAtomFamily(entity));

  if (shouldShowSelectedOnlyRows) {
    const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));

    if (limitIds) {
      // Set, not `selectedIds.includes`: with starred-only also on, both sides can run to thousands.
      const selectedIdsSet = new Set(selectedIds);
      limitIds = limitIds.filter(id => selectedIdsSet.has(id));
    } else {
      limitIds = selectedIds;
    }
  }

  if (skipFilters) {
    if (limitIds !== undefined) {
      const limitIdsSet = new Set(limitIds);
      return rows.filter(row => limitIdsSet.has(row.id));
    }

    return rows;
  }

  let clientSearch: ITableClientSearch | undefined;

  if (!isServerPaginated) {
    clientSearch = {
      value: searchValue,
      allowedFieldNamesResolved: searchableFilters,
      phoneFieldNamesResolved: get(filterSearchablePhoneFieldNamesSF(entity)),
    };
  }

  return TableHelpers.getFilteredRows({
    rows,
    filters: clientFilter,
    limitIds,
    clientSearch,
  });
};

/** The one memoized entry point for the client filter pipeline. */
const tableRowsFilteredOnceSF = boundedSelectorFamily<
  IExtendableDto[],
  RydeUiUnit
>({
  key: "tableRowsFilteredOnceSF",
  get:
    entity =>
    ({ get }) =>
      selectTableRowsFilteredOnce({ get, entity }),
});

export const tableFilteredGroupedRowsMapSF = boundedSelectorFamily<
  TTableFilteredGroupedRowsMap,
  RydeUiUnit
>({
  key: "tableFilteredGroupedRowsMapSF",
  get:
    entity =>
    ({ get }) =>
      resolveTableFilteredGroupedRowsMap({
        entity,
        getServerGroupedRows: () => get(tableRowsGroupValuesAtomFamily(entity)),
        getClientFilteredRows: () => ({
          filteredRows: get(tableRowsFilteredOnceSF(entity)),
          groupingFieldNameResolved: get(tableGroupingFieldResolvedSF(entity)),
        }),
      }),
});

/** Grid row path for the active grouping column (resolves live-map `statusLate` control id). */
export const tableGroupingFieldResolvedSF = selectorFamily<
  string | undefined,
  RydeUiUnit
>({
  key: "tableGroupingFieldResolvedSF",
  get:
    entity =>
    ({ get }) => {
      const columnGrouping = get(tableGroupingAF(entity));
      const groupingField = columnGrouping[0];

      if (
        groupingField === undefined ||
        groupingField === NO_GROUPING_QS_VALUE
      ) {
        return undefined;
      }

      const gridControls = get(gridControlsSF(entity));

      return (
        resolveRideMapDelayStatusGroupingFieldName(
          groupingField,
          gridControls,
        ) ?? String(groupingField)
      );
    },
});

export const tableFilteredGroupedRowsSF = boundedSelectorFamily<
  IExtendableDto[],
  [
    RydeUiUnit,
    DtoFieldType | typeof GROUPING_GRID_NO_GROUPS,
    string | number | undefined,
  ]
>({
  key: "tableFilteredGroupedRowsSF",
  get:
    ([entity, groupingFieldValue, groupingFieldNameResolved]) =>
    ({ get }) => {
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));

      return [
        ...getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue,
          groupingFieldNameResolved,
        }),
      ];
    },
});

export const tableFilteredAllGroupsRowsIdsSF = boundedSelectorFamily<
  Set<number>,
  RydeUiUnit
>({
  key: "tableFilteredAllGroupsRowsIdsSF",
  get:
    entity =>
    ({ get }) => {
      const filteredRows = get(tableRowsFilteredOnceSF(entity));
      return new Set(filteredRows.map(row => row.id));
    },
});

const tableRowsFilteredIgnoringSearchOnceSF = boundedSelectorFamily<
  IExtendableDto[],
  RydeUiUnit
>({
  key: "tableRowsFilteredIgnoringSearchOnceSF",
  get:
    entity =>
    ({ get }) =>
      selectTableRowsFilteredOnce({
        get,
        entity,
        isIgnoreSearch: true,
        isIgnoreSelectedOnly: true,
      }),
});

export const tableSelectionScopeRowsIdsSF = boundedSelectorFamily<
  Set<number>,
  RydeUiUnit
>({
  key: "tableSelectionScopeRowsIdsSF",
  get:
    entity =>
    ({ get }) => {
      if (!isEntitySelectionKeptOnSearch(entity)) {
        return get(tableFilteredAllGroupsRowsIdsSF(entity));
      }

      const rowsIgnoringSearch = get(
        tableRowsFilteredIgnoringSearchOnceSF(entity),
      );

      return new Set(rowsIgnoringSearch.map(row => row.id));
    },
});

export const tableGroupValuesSF = boundedSelectorFamily<
  IExtendableDto[keyof IExtendableDto][] | typeof GROUPING_GRID_NO_GROUPS,
  RydeUiUnit
>({
  key: "tableGroupValuesSF",
  get:
    entity =>
    ({ get }) => {
      if (isEntityServerSidePaginated(entity)) {
        const columnGrouping = get(tableGroupingAF(entity));
        const groupingField = columnGrouping[0];

        if (
          groupingField === undefined ||
          groupingField === NO_GROUPING_QS_VALUE
        ) {
          return GROUPING_GRID_NO_GROUPS;
        }

        const tableServerGroupValues = get(tableServerGroupValuesAf(entity));

        if (!tableServerGroupValues.length) {
          return GROUPING_GRID_NO_GROUPS;
        }

        return tableServerGroupValues.map(el => el.value);
      }

      const columnSorting = get(tableColumnsSortingAF(entity));
      const rows = get(tableRowsComputedSF(entity));
      const columnGrouping = get(tableGroupingAF(entity));
      const groupingField = columnGrouping[0];
      const gridControls = get(gridControlsSF(entity));
      const groupingFieldResolved = get(tableGroupingFieldResolvedSF(entity));
      const groupingDirection = get(tableGroupingDirectionDescAF(entity))
        ? "desc"
        : "asc";

      if (
        groupingField === undefined ||
        groupingField === NO_GROUPING_QS_VALUE
      ) {
        return GROUPING_GRID_NO_GROUPS;
      }

      const uniqueGroupValues = TableHelpers.getUniqueGroupValues(
        (rows as IExtendableDto[]) || [],
        groupingFieldResolved,
      );
      const groupValuesFromTable = TableHelpers.getOrderedGroupValues(
        uniqueGroupValues,
        groupingDirection,
      );

      if (!groupValuesFromTable?.length) {
        return GROUPING_GRID_NO_GROUPS;
      }

      if (
        entity === "rideMap" &&
        isRideMapDelayStatusGroupingField(groupingField, gridControls)
      ) {
        return getOrderedLiveMapDelayStatusGroupValues(
          groupValuesFromTable,
          groupingDirection,
        );
      }

      const sortingStateForGroupedField = columnSorting.find(
        ({ id }) => groupingField === id,
      );

      if (sortingStateForGroupedField) {
        return getSortedGroupValuesFromTable(
          groupValuesFromTable,
          sortingStateForGroupedField,
        );
      }

      return groupValuesFromTable;
    },
});

export const isSelectedTableRowsIdsExpandedSF = selectorFamily<
  boolean,
  RydeUiUnit
>({
  key: "isSelectedTableRowsIdsExpandedSF",
  get:
    entity =>
    ({ get }) => {
      const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));

      if (!selectedIds.length) {
        return false;
      }

      const gridExpanded = get(gridExpandedAF(entity));

      if (!hasKeys(gridExpanded)) {
        return false;
      }

      const expandedIds = new Set([...Object.keys(gridExpanded).map(Number)]);

      return selectedIds.every(id => expandedIds.has(id));
    },
});

export const expandedIdsSF = selectorFamily<number[], RydeUiUnit>({
  key: "expandedIdsSF",
  get:
    entity =>
    ({ get }) => {
      const gridExpanded = get(gridExpandedAF(entity));

      return [...Object.keys(gridExpanded).map(Number)];
    },
});

export const selectedAndExpandedIdsSF = selectorFamily<number[], RydeUiUnit>({
  key: "selectedAndExpandedIdsSF",
  get:
    entity =>
    ({ get }) => {
      const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));
      const gridExpanded = get(gridExpandedAF(entity));

      return unique([...Object.keys(gridExpanded).map(Number), ...selectedIds]);
    },
});

/** Per-row expand read/toggle — only the affected row re-renders (mirrors `isStarredTableRowIdSF`). */
export const isRowExpandedSF = selectorFamily<
  boolean,
  [entity: RydeUiUnit, id: number]
>({
  key: "isRowExpandedSF",
  get:
    ([entity, id]) =>
    ({ get }) =>
      Boolean(get(gridExpandedAF(entity))[id]),
  set:
    ([entity, id]) =>
    ({ set }, isExpanded) => {
      // Reset collapses; presence in the map is what TanStack treats as expanded.
      const shouldExpand = guardIsRecoilDefaultValue(isExpanded)
        ? false
        : isExpanded;

      set(gridExpandedAF(entity), prevState => {
        const updatedState = { ...prevState };

        if (shouldExpand) {
          updatedState[id] = true;
        } else {
          delete updatedState[id];
        }

        return updatedState;
      });
    },
});

export const tableFilteredGroupedCount = boundedSelectorFamily<
  number,
  RydeUiUnit
>({
  key: "tableFilteredGroupedCount",
  get:
    entity =>
    ({ get }) => {
      const groupValues = get(tableGroupValuesSF(entity));

      if (groupValues === GROUPING_GRID_NO_GROUPS) {
        return 0;
      }

      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));

      return groupValues.reduce((acc, groupValue) => {
        const groupedRows = getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue: groupValue,
          groupingFieldNameResolved: get(tableGroupingFieldResolvedSF(entity)),
        });

        return acc + groupedRows.length;
      }, 0);
    },
});

export const rideStartEndPositionSelector = selector({
  key: "rideStartEndPositionSelector",
  get: ({ get }) => {
    return get(rideStartEndPositionAtom);
  },
});

export const tableColumnsSortingSF = selectorFamily<SortingState, RydeUiUnit>({
  key: "tableColumnsSortingSF",
  get:
    entity =>
    ({ get }) => {
      const tableColumnsSorting = get(tableColumnsSortingAF(entity));
      return tableColumnsSorting;
    },
});

export const tableGroupingSF = selectorFamily<GroupingState, RydeUiUnit>({
  key: "tableGroupingSF",
  get:
    entity =>
    ({ get }) => {
      const tableGrouping = get(tableGroupingAF(entity));
      return tableGrouping;
    },
});

export const tableColumnsOrderStateSF = selectorFamily<
  ColumnOrderState,
  RydeUiUnit
>({
  key: "tableColumnsOrderStateSF",
  get:
    entity =>
    ({ get }) => {
      const tableColumnsOrderState = get(tableColumnsOrderStateAF(entity));
      return tableColumnsOrderState;
    },
});

export const tableRowsGroupServerCountSf = selectorFamily<
  number,
  [groupingValue: string | null | undefined, RydeUiUnit]
>({
  key: "tableRowsGroupServerCountSf",
  get:
    ([groupingValue, entity]) =>
    ({ get }) => {
      const tableServerGroupValues = get(tableServerGroupValuesAf(entity));
      const count = tableServerGroupValues.find(
        x => x.value === groupingValue,
      )?.count;

      return parseInt(count ?? "0", DECIMAL_RADIX);
    },
});

export const tableRowsPaginationSf = selectorFamily<
  IGroupValuePaginationState[],
  RydeUiUnit
>({
  key: "tableRowsPaginationSf",
  get:
    entity =>
    ({ get }) => {
      const tableServerGroupValues = get(tableServerGroupValuesAf(entity));
      const result: IGroupValuePaginationState[] = [];

      tableServerGroupValues.forEach(groupValue => {
        const pagination = get(
          tableItemPaginationAF([groupValue.value ?? "", entity]),
        );

        if (pagination) {
          result.push({
            ...pagination,
            value: groupValue.value,
          });
        }
      });

      return result;
    },
  set:
    entity =>
    ({ set }, newGroupValuesPagination) => {
      if (Array.isArray(newGroupValuesPagination)) {
        newGroupValuesPagination.forEach(el => {
          set(tableItemPaginationAF([el.value ?? "", entity]), el);
        });
      }
    },
});
